import { useEffect } from 'react';

import { AlertTriangle, Info } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { MonthDayPicker } from '@/modules/common/components/molecules/date/month-day-picker';
import { InputFieldWithCurrency } from '@/modules/common/components/molecules/inputs/input-field-with-currency';
import { CustomRadioGroup } from '@/modules/common/components/molecules/selectors/radio-group';

import {
  FORM_FIELD_ALLOCATION_DAYS,
  FORM_FIELD_ALLOCATION_FORMAT,
  FORM_FIELD_DATE_OF_RENEWAL,
} from '../../../../constants/time-off-type-form-fields';
import { TimeOffAllocationFormat } from '../../../../types/time-off';
import {
  ALLOCATION_DAYS_MIN_VALUE,
  ALLOCATION_DAYS_MONTHLY_MAX_DECIMAL_PLACES,
  ALLOCATION_DAYS_MONTHLY_MAX_VALUE,
  ALLOCATION_DAYS_YEARLY_MAX_DECIMAL_PLACES,
  ALLOCATION_DAYS_YEARLY_MAX_VALUE,
  TimeOffTypeSetupFormData,
} from '../../../../validations/time-off-type-validation';

interface AllocationSettingsSectionProps {
  form: UseFormReturn<TimeOffTypeSetupFormData>;
  isReadMode: boolean;
  initialFormat: TimeOffAllocationFormat;
}

export function AllocationSettingsSection({
  form,
  isReadMode,
  initialFormat,
}: AllocationSettingsSectionProps) {
  const { t } = useTranslation('authenticated.settings');

  const currentFormat = form.watch(FORM_FIELD_ALLOCATION_FORMAT);
  const allocationDays = form.watch(FORM_FIELD_ALLOCATION_DAYS);
  const isYearly = currentFormat === TimeOffAllocationFormat.Yearly;
  const formatChanged = currentFormat !== initialFormat;

  useEffect(() => {
    void form.trigger(FORM_FIELD_ALLOCATION_DAYS);
  }, [currentFormat, form]);

  useEffect(() => {
    if (isYearly) return;

    const renewalDate = form.getValues(FORM_FIELD_DATE_OF_RENEWAL);
    if (renewalDate.day !== 1 || renewalDate.month !== 1) {
      form.setValue(FORM_FIELD_DATE_OF_RENEWAL, { month: 1, day: 1 }, { shouldDirty: true });
    }
  }, [form, isYearly]);

  const suffixText = isYearly
    ? t('pages.timeOff.details.sections.allocation.daysPerYearSuffix')
    : t('pages.timeOff.details.sections.allocation.daysPerMonthSuffix');

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div>
          <h3 className="text-md font-semibold">
            {t('pages.timeOff.details.sections.allocation.title')}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('pages.timeOff.details.sections.allocation.description')}
          </p>
        </div>

        <CustomRadioGroup
          name={FORM_FIELD_ALLOCATION_FORMAT}
          control={form.control}
          label={t('pages.timeOff.details.sections.allocation.format')}
          disabled={isReadMode}
          options={[
            {
              value: TimeOffAllocationFormat.Yearly,
              label: t('pages.timeOff.details.sections.allocation.formatYearly'),
            },
            {
              value: TimeOffAllocationFormat.Monthly,
              label: t('pages.timeOff.details.sections.allocation.formatMonthly'),
            },
          ]}
        />

        {formatChanged && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t('pages.timeOff.details.sections.allocation.formatChangedWarning')}
            </AlertDescription>
          </Alert>
        )}

        <MonthDayPicker
          name={FORM_FIELD_DATE_OF_RENEWAL}
          control={form.control}
          label={t('pages.timeOff.details.sections.allocation.dateOfRenewal')}
          tooltip={
            !isYearly
              ? t('pages.timeOff.details.sections.allocation.dateOfRenewalMonthlyTooltip')
              : undefined
          }
          disabled={isReadMode || !isYearly}
          displayValueOverride={
            !isYearly
              ? t('pages.timeOff.details.sections.allocation.dateOfRenewalMonthlyValue')
              : undefined
          }
        />

        <div className="space-y-1.5">
          <InputFieldWithCurrency
            name={FORM_FIELD_ALLOCATION_DAYS}
            label={t('pages.timeOff.details.sections.allocation.numberOfDays')}
            control={form.control}
            suffixText={suffixText}
            isReadMode={isReadMode}
            maxValue={
              isYearly ? ALLOCATION_DAYS_YEARLY_MAX_VALUE : ALLOCATION_DAYS_MONTHLY_MAX_VALUE
            }
            maxDecimalPlaces={
              isYearly
                ? ALLOCATION_DAYS_YEARLY_MAX_DECIMAL_PLACES
                : ALLOCATION_DAYS_MONTHLY_MAX_DECIMAL_PLACES
            }
            minValue={ALLOCATION_DAYS_MIN_VALUE}
          />
          {!isYearly && !!allocationDays && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="h-3 w-3 shrink-0" />
              {t('pages.timeOff.details.sections.allocation.monthlyDaysPerYearHint', {
                days: Math.round(allocationDays * 12 * 100) / 100,
              })}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
